import React, { Component } from 'react'
import { getMerchantInfo, HLJ_STATISTIC, getVisibilityChangeProp } from '@util'
import moment from 'moment'
import { isTestEnv, getCookie } from '@hlj/hlj-utils'

const { hidden, visibilityChange } = getVisibilityChangeProp()

/**
 * 190116 新需求, 路由进入时统一埋点
 * ext 传的是路由上的 queryString
 */
const statOnRouteChange = async ({ page_name, ext = {} }) => {
  // 开发模式、测试环境不发送
  if (__DEV__ || isTestEnv() || !page_name) return

  HLJ_STATISTIC({
    event_type: 'page_jump',
    page_name,
    ext
  })
}

const getDurationStatData = ({ page_name, duration }) => {
  const { id, city_code, is_bd } = getMerchantInfo()

  return {
    event_type: 'page_duration',
    app_name: 'web',
    user_id: id,
    page_name,
    page_data: {
      ext: {
        account: is_bd == 1 ? getCookie('username') : ''
      }
    },
    duration,
    user_city: city_code,
    time_stamp: moment().format()
  }
}

/**
 * 停留时长埋点
 */
const statOnDuration = ({ duration, page_name }) => {
  // 开发模式、测试环境不发送
  if (__DEV__ || isTestEnv() || !page_name) return

  HLJ_STATISTIC(getDurationStatData({ duration, page_name }))
}

/**
 * 全局埋点高阶组件：
 * 1. 页面（路由）切换时自动埋点
 * 2. 页面停留时长统计
 */
function withStatistic(WrappedComponent) {
  return class extends Component {
    constructor(props) {
      super(props)

      // 页面进入时间
      this.enterTime = moment()
      const { id } = getMerchantInfo()
      this.lastDurationStorageKey = `LAST_DURATION_DATA_${id}`
    }

    componentDidMount() {
      window.addEventListener('unload', this.handleUnload)
      document.addEventListener(visibilityChange, this.handleVisibleChange)

      // 首次进入时 page_jump 埋点
      statOnRouteChange(this.getRouteStatKey(this.props))

      // 如果上次刷新或关闭浏览器时存储了时长埋点数据，则上报，上报完清除
      const lastDurationData = localStorage.getItem(this.lastDurationStorageKey)
      if (lastDurationData) {
        HLJ_STATISTIC(JSON.parse(lastDurationData))
        localStorage.removeItem(this.lastDurationStorageKey)
      }
    }

    shouldComponentUpdate(nextProps) {
      return this.isRouteChange(nextProps)
    }

    /**
     * 当路由切换时页面埋点：
     * 1. 新页面进入时 page_jump 埋点
     * 2. 旧路由停留时长埋点
     */
    componentDidUpdate(prevProps) {
      if (this.isRouteChange(prevProps)) {
        this.handleRouteDurationStat(prevProps)
        statOnRouteChange(this.getRouteStatKey(this.props))
      }
    }

    componentWillUnmount() {
      document.removeEventListener(visibilityChange, this.handleVisibleChange)
      window.removeEventListener('unload', this.handleUnload)
    }

    isRouteChange = nextProps => {
      return nextProps.location.pathname != this.props.location.pathname
    }

    /**
     * 获取当前路由上的 stat 参数
     */
    getRouteStatKey = props => {
      if (!props.routes) {
        return
      }

      const { search, pathname } = props.location
      const queryString = search.substr(1, search.length)

      const list = props.routes.slice().reverse()
      let stat = ''
      for (let i = 0; i < list.length; i++) {
        if (list[i].stat) {
          stat = list[i].stat
          break
        }
      }

      return {
        ext: { target_url: pathname, title: queryString },
        page_name: stat
      }
    }

    getDurationAndPageName = props => {
      const duration = moment().diff(this.enterTime, 'milliseconds')
      const { page_name } = this.getRouteStatKey(props)

      return {
        duration,
        page_name
      }
    }

    /**
     * 停留时长埋点
     */
    handleRouteDurationStat = (props, reset = false) => {
      statOnDuration(this.getDurationAndPageName(props))

      // 由于 isHidden 比 unload 先执行，所以这里用 setTimeout 等 unload 执行完后在清除 enterTime
      setTimeout(() => {
        this.enterTime = reset ? null : moment()
      })
    }

    /**
     * 处理切换浏览器 tab，最小化浏览器等使当前页面不可见的场景
     * 1. 当前页面不可见时，发送当前页面停留时长埋点，并重置 enterTime
     * 2. 当前页面可见时，将 enterTime 设为当前时间
     *
     * PS：当刷新页面或关闭浏览器时，该方法也会触发，但浏览器会阻止此情况下的埋点
     * 这种场景交给 unload 事件处理
     */
    handleVisibleChange = event => {
      const isHidden = document[hidden]
      if (isHidden) {
        this.handleRouteDurationStat(this.props, true)
      } else {
        this.enterTime = moment()
      }
    }

    /**
     * 关闭浏览器和刷新时，暂存上次埋点的数据
     * 下次进入时上报
     */
    handleUnload = () => {
      const data = this.getDurationAndPageName(this.props)
      localStorage.setItem(
        this.lastDurationStorageKey,
        JSON.stringify(getDurationStatData(data))
      )
    }

    render() {
      return <WrappedComponent {...this.props} />
    }
  }
}

export default withStatistic
